import { onError } from '@apollo/client/link/error';
import { clearAuth } from '@auth/state';
import { track } from '@core/logging';
import { routes } from '@core/routes';
import { store } from '@store';
import type { GraphQLFormattedError } from 'graphql';

/**
 * Check if a GraphQL error is an authentication error
 */
function isAuthenticationError(code: unknown): boolean {
  return code === 'UNAUTHENTICATED' || (typeof code === 'string' && (code.startsWith('AUTH_') || code.startsWith('SESSION_')));
}

/**
 * Handle authentication error by clearing auth state and redirecting to login
 */
function handleAuthenticationError() {
  // Clear authentication state
  store.dispatch(clearAuth());

  // Redirect to login with current location for redirect after login
  const currentPath = window.location.pathname + window.location.search;
  const loginUrl = `${routes.login}?redirectTo=${encodeURIComponent(currentPath)}`;

  // Only redirect if we're not already on the login page
  if (!window.location.pathname.startsWith(routes.login)) {
    track('AuthErrorRedirect');
    window.location.href = loginUrl;
  }
}

/**
 * Handle GraphQL authentication errors
 */
function handleGraphQLAuthErrors(graphQLErrors: readonly GraphQLFormattedError[]) {
  for (const error of graphQLErrors) {
    if (!isAuthenticationError(error.extensions?.code)) {
      continue;
    }

    handleAuthenticationError();

    return true; // Handled auth error
  }

  return false;
}

/**
 * Handle network authentication errors
 */
function handleNetworkAuthErrors(networkError: Error & { statusCode?: number }) {
  if (!networkError || !('statusCode' in networkError)) {
    return;
  }

  const statusCode = networkError.statusCode;

  if (!statusCode || ![401, 403].includes(statusCode)) {
    return;
  }

  handleAuthenticationError();
}

/**
 * Create an Apollo Link that detects authentication errors and handles them directly
 * @returns Apollo Link for handling authentication errors
 */
export function createAuthErrorLink() {
  return onError(({ graphQLErrors, networkError }) => {
    // Handle GraphQL authentication errors first
    if (graphQLErrors && handleGraphQLAuthErrors(graphQLErrors)) {
      return; // Exit early if auth error was handled
    }

    // Handle network authentication errors
    if (networkError) {
      handleNetworkAuthErrors(networkError as Error & { statusCode?: number });
    }
  });
}
